import * as Sentry from "@sentry/browser";

import "@popperjs/core";

import "bootstrap/js/dist/alert";
import "bootstrap/js/dist/button";
import "bootstrap/js/dist/carousel";
import "bootstrap/js/dist/collapse";
import "bootstrap/js/dist/dropdown";
import "bootstrap/js/dist/tab";

import "./vendors/fontawesome";

import { initAutocomplete } from "./components/autocomplete";
import { initAutosize } from "./components/autosize";
import { initCookieConsentBanner } from "./components/cookie-consent-banner";
import { initDialogs } from "./components/dialogs";
import { initFormValidation } from "./components/form-validation";
import { initInputsValidation } from "./components/form-words-validation";
import { initGoToUrl } from "./components/go-to-url";
import { initHeaderSearch } from "./components/header-search";
import { initNextSteps } from "./components/next-steps";
import { initRiveAnimations } from "./components/rive/all-animations";
import { initializeFavoriteRive } from "./components/rive/favorite-btn";
import { initProfileReferencesSlider } from "./components/sliders/profile-references-slider";
import { initProfileReviewsSlider } from "./components/sliders/profile-reviews-slider";
import { initSlider } from "./components/sliders/slider";
import { initToolTips } from "./components/tooltips";
import { initCountryAndLanguage } from "./layout/footer/country-language";
import { initFooterFixed } from "./layout/footer/footer-fixed";
import { initHomePageHeader } from "./layout/header/homepage-header";
import { initHeaderNav } from "./layout/header-nav";
import { initAccount } from "./pages/account/account";
import { initAccountAdsConsent } from "./pages/account/ads-consent";
import { initAccountNotificationUpdate } from "./pages/account/notification-update";
import { initAccountPaymentsPayouts } from "./pages/account/payments-payouts";
import { initAuth } from "./pages/auth/auth";
import { initSignupCompleteForm } from "./pages/auth/signup-complete-form";
import { initBadges } from "./pages/badges";
import { initBookingAddonTime } from "./pages/booking/booking-addon-time";
import { initBookingCreate } from "./pages/booking/booking-create";
import { initBookingInvite } from "./pages/booking/booking-invite";
import { initBookingResource } from "./pages/booking/booking-resource";
import { initBookingAccountTaxes } from "./pages/booking-account-taxes";
import { initContact } from "./pages/contact";
import { initConversationCannedResponse } from "./pages/conversation/canned-responses";
import { initConversationMessageList } from "./pages/conversation/message-list";
import { initConversationMessaging } from "./pages/conversation/messaging";
import { initConversationTextareaCookieValue } from "./pages/conversation/textarea-cookie-value";
import { initConversationUseTheApp } from "./pages/conversation/use-the-app";
import { initDocumentAdd } from "./pages/document/document-add";
import { initDocumentDelete } from "./pages/document/document-delete";
import { initHomepageDialogNextSteps } from "./pages/homepage/dialog-next-steps";
import { initHomepageDialogReviewRequest } from "./pages/homepage/dialog-review-request";
import { initHomepageProfileResearchOptIn } from "./pages/homepage/profile-research-opt-in";
import { initHowItWorksStepper } from "./pages/how_it_works_stepper";
import { initLandingAutocomplete } from "./pages/landing/autocomplete";
import { initShareHomepageSwapText } from "./pages/landing/homepage_swap_text";
import { initNpsSurvey } from "./pages/nps-survey";
import { initChangePlan } from "./pages/premium/change-plan";
import { initPremiumReview } from "./pages/premium/premium-review";
import { initPremiumUpgrade } from "./pages/premium/premium-upgrade";
import { initProfile } from "./pages/profile/profile";
import { initProfileContact } from "./pages/profile/profile-contact";
import { initProfileContactReminder } from "./pages/profile/profile-contact-reminder";
import { initProfileDescription } from "./pages/profile/profile-description";
import { initProfileMap } from "./pages/profile/profile-map";
import { initProfileTranslate } from "./pages/profile/profile-translate";
import { initProfileViewedCookie } from "./pages/profile/profile-viewed-cookie";
import { initProfileEditAvailability } from "./pages/profile-edit/availability";
import { initProfileEditDialogAvailability } from "./pages/profile-edit/dialog-availability";
import { initProfileEditVideo } from "./pages/profile-edit/profile-video";
import { initAddReference } from "./pages/reference";
import { initReferenceInvite } from "./pages/reference-invite";
import { initSearchDoubleSlider } from "./pages/search/double-slider";
import { initSavedSearchForm } from "./pages/search/saved-search-form";
import { initSavedSearchList } from "./pages/search/saved-search-list";
import { initSearch } from "./pages/search/search";
import { initSearchSpecialNeeds } from "./pages/search/special-needs";
import { shareYourExperience } from "./pages/share-your-experience";
import { initCustomPricing } from "./pages/work/custom-pricing";
import { initWorkSignup } from "./pages/work/signup";
import { initAbTest } from "./utils/ab-tests";
import { initCopyToClipboard } from "./utils/clipboard";
import { initCompanyDetailsEmailUnmask } from "./utils/company-details-email-unmask";
import { initPhoneSmsVerification } from "./utils/phone_sms_verification";
import { initPricesExpand } from "./utils/pricing-expand";
import { initRating } from "./utils/rating";
import { sentryInit } from "./utils/sentry";
import { initShare } from "./utils/share";
import { initTimeFormat } from "./utils/time-format";

// Pages that report to Sentry, each recognised by an element its own init
// function already requires (initSearch, initAutocomplete's homepage instance,
// initProfile) or by the page's root wrapper (the booking pages). Removing that
// element breaks the page visibly, so reporting can't be dropped by accident the
// way a dedicated template attribute could.
// Only the first match initializes; add pages here one at a time.
const SENTRY_PAGES: ReadonlyArray<readonly [name: string, isCurrentPage: () => boolean]> = [
  ["search", () => document.getElementById("search") !== null],
  ["homepage-search-form", () => document.getElementById("autocomplete-homepage-1") !== null],
  ["profile", () => document.getElementById("profile") !== null],
  // Bookings overview (Booking/retrieve_all.html.twig, hosts the create form) and the
  // booking detail page (Booking/retrieve.html.twig, hosts the add-on time dialog).
  // The inbox thread also embeds the create form but is the conversation page, not this one.
  [
    "booking",
    () =>
      document.getElementById("booking-collection") !== null ||
      document.getElementById("booking-resource") !== null,
  ],
  // Inbox thread (Inbox/MessageThread/index.html.twig), the root wrapper messaging needs.
  ["conversation", () => document.getElementById("conversation") !== null],
  // Login/signup page (Auth/auth.html.twig) and the signup completion page
  // (Auth/SignupComplete/index.html.twig). The login dialog on other pages is #dialog-auth.
  [
    "auth",
    () =>
      document.getElementById("auth") !== null ||
      document.getElementById("signup-complete") !== null,
  ],
  // Upgrade pages (Premium/Upgrade/upgrade_v3 and upgrade_agency_v3), the review step
  // (Premium/write_review) and the active-subscription page for Stripe billing only: its
  // only script hook is the change-plan dialog, which Premium/subscription_active includes
  // in the PROVIDER_STRIPE_BILLING branch alone. Apple, Google Play, Free, Prepaid and Work
  // subscriptions render no hook there and stay untracked.
  [
    "premium",
    () =>
      document.getElementById("upgrade-page") !== null ||
      document.getElementById("premium-review") !== null ||
      document.getElementById("dialog-change-plan") !== null,
  ],
];

document.addEventListener("DOMContentLoaded", () => {
  // One init per page, before any initializer runs, so init failures are captured
  // too. On other pages Sentry is never initialized and captureException() below
  // is a no-op, so untracked pages can't flood the project.
  const sentryPage = SENTRY_PAGES.find(([, isCurrentPage]) => isCurrentPage());

  if (sentryPage) {
    sentryInit(sentryPage[0]);
  }

  const initializers = [
    //utils
    initTimeFormat,
    initShare,
    initAbTest,
    initCopyToClipboard,
    initCompanyDetailsEmailUnmask,
    initFooterFixed,
    initPricesExpand,
    initRating,
    initPhoneSmsVerification,
    initGoToUrl,
    initNextSteps,

    //Rive
    initRiveAnimations,
    initializeFavoriteRive,

    //Component
    initSlider,
    initProfileReviewsSlider,
    initProfileReferencesSlider,
    initDialogs,
    initCookieConsentBanner,
    initHeaderSearch,
    initAutocomplete,
    initFormValidation,
    initAutosize,
    initToolTips,
    initInputsValidation,

    //Layout
    initHeaderNav,
    initCountryAndLanguage,
    initHomePageHeader,

    //Pages
    initBadges,
    initNpsSurvey,
    initContact,
    initReferenceInvite,
    initAddReference,
    initWorkSignup,
    initCustomPricing,
    shareYourExperience,
    initHomepageDialogReviewRequest,
    initHomepageProfileResearchOptIn,
    initHomepageDialogNextSteps,
    initShareHomepageSwapText,
    initLandingAutocomplete,
    initProfileContact,
    initProfileContactReminder,
    initProfileDescription,
    // Must run before initSearch: both attach an "input" listener to the same slider elements,
    // and listeners on the same element fire in attachment order. This one clamps the dragged
    // value back in range; initSearch's listener reads that value to update the filter's status
    // text, so it needs to see the already-clamped value, not the raw mid-drag one.
    initSearchDoubleSlider,
    initSearch,

    initConversationTextareaCookieValue,
    initConversationUseTheApp,
    initConversationCannedResponse,
    initConversationMessaging,
    initConversationMessageList,

    initProfile,
    initProfileTranslate,
    initProfileViewedCookie,
    initProfileMap,

    initAuth,
    initSignupCompleteForm,

    initDocumentAdd,
    initDocumentDelete,

    initAccount,
    initAccountPaymentsPayouts,
    initAccountNotificationUpdate,
    initAccountAdsConsent,

    initChangePlan,
    initPremiumReview,
    initPremiumUpgrade,

    initProfileEditVideo,
    initProfileEditAvailability,
    initProfileEditDialogAvailability,

    initBookingAddonTime,
    initBookingCreate,
    initBookingInvite,
    initBookingResource,
    initBookingAccountTaxes,

    initHowItWorksStepper,

    initSavedSearchList,
    initSavedSearchForm,
    initSearchSpecialNeeds,
  ];

  initializers.forEach((fn) => {
    try {
      fn();
    } catch (error) {
      console.error(`❌ ${fn.name} failed:`, error);
      Sentry.captureException(error);
    }
  });
});
